/**
 * Maps Stytch and general authentication errors to friendly, human-readable messages.
 * Prevents raw JSON error objects or strings from leaking to the UI.
 */

const STYTCH_ERROR_MAP: Record<string, string> = {
  incorrect_password: "The password you entered is incorrect. Please try again.",
  invalid_credentials: "Incorrect email or password. Please try again.",
  email_not_found: "No account found with this email. Please check the spelling or sign up.",
  user_not_found: "No account found with this email. Please check the spelling or sign up.",
  duplicate_email: "An account with this email already exists. Please sign in instead.",
  user_already_exists: "An account with this email already exists. Please sign in instead.",
  invalid_email: "Please enter a valid email address.",
  password_too_short: "Password must be at least 8 characters long.",
  invalid_password: "Password must be at least 8 characters long.",
  breached_password: "This password has appeared in a known data breach. Please choose a more secure password.",
  otp_code_invalid: "The verification code entered is incorrect. Please check and try again.",
  otp_code_expired: "The verification code has expired. Please request a new one.",
  otp_code_not_found: "Verification code not found. Please request a new code.",
  reset_password_invalid_token: "This password reset link is invalid or has expired.",
  invalid_token: "This link is invalid or has expired.",
  rate_limit_exceeded: "Too many attempts. Please wait a minute and try again.",
  too_many_requests: "Too many attempts. Please wait a minute and try again.",
  session_not_found: "Your session has expired. Please sign in again.",
  session_expired: "Your session has expired. Please sign in again.",
};

function tryParseJson(str: unknown): Record<string, any> | null {
  if (typeof str !== "string") return null;
  const trimmed = str.trim();
  if (!trimmed.startsWith("{") || !trimmed.endsWith("}")) return null;
  try {
    const parsed = JSON.parse(trimmed);
    return typeof parsed === "object" && parsed !== null ? parsed : null;
  } catch {
    return null;
  }
}

export function getAuthErrorMessage(error: unknown): string {
  if (!error) return "Something went wrong. Please try again.";

  let errorType: string | null = null;
  let rawMessage: string | null = null;

  // 1. If error is an object
  if (typeof error === "object" && error !== null) {
    // Check if error.message is stringified JSON
    const msg = Reflect.get(error, "message");
    const parsedMsg = tryParseJson(msg);

    if (parsedMsg) {
      errorType = parsedMsg.error_type || parsedMsg.type || parsedMsg.code || null;
      rawMessage = parsedMsg.error_message || parsedMsg.message || null;
    } else {
      rawMessage = typeof msg === "string" ? msg : null;
    }

    // Direct Stytch error properties
    const directType = Reflect.get(error, "error_type") || Reflect.get(error, "code");
    if (typeof directType === "string") {
      errorType = directType;
    }

    const directMsg = Reflect.get(error, "error_message");
    if (typeof directMsg === "string") {
      rawMessage = directMsg;
    }

    // Check errors array (e.g. from validation or GraphQL/Stytch response)
    const maybeErrors = Reflect.get(error, "errors");
    if (Array.isArray(maybeErrors) && maybeErrors.length > 0) {
      const first = maybeErrors[0];
      if (typeof first === "object" && first !== null) {
        const firstType = Reflect.get(first, "error_type") || Reflect.get(first, "code");
        if (typeof firstType === "string") errorType = firstType;

        const firstMsg =
          Reflect.get(first, "error_message") ||
          Reflect.get(first, "longMessage") ||
          Reflect.get(first, "message");
        if (typeof firstMsg === "string") rawMessage = firstMsg;
      }
    }
  } else if (typeof error === "string") {
    // If error itself is a stringified JSON
    const parsed = tryParseJson(error);
    if (parsed) {
      errorType = parsed.error_type || parsed.type || parsed.code || null;
      rawMessage = parsed.error_message || parsed.message || null;
    } else {
      rawMessage = error;
    }
  }

  // 2. Check known error types in map
  if (errorType) {
    const directMatch = STYTCH_ERROR_MAP[errorType];
    if (directMatch) {
      return directMatch;
    }

    const normalizedType = errorType.toLowerCase().replace(/[- ]/g, "_");
    const normalizedMatch = STYTCH_ERROR_MAP[normalizedType];
    if (normalizedMatch) {
      return normalizedMatch;
    }
  }

  // 3. If rawMessage is human-readable (not JSON or code dump)
  if (rawMessage && typeof rawMessage === "string") {
    const trimmed = rawMessage.trim();
    // Verify it's not still JSON
    if (!trimmed.startsWith("{") && !trimmed.startsWith("[") && trimmed.length > 0) {
      // Remove any trailing technical error codes if present
      return trimmed;
    }
  }

  return "Something went wrong. Please check your details and try again.";
}
